import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog'

const INVOICE_STATE_MESSAGES = {
  none: 'This sale has not been invoiced yet. The line item will be updated directly.',
  invoiced: 'This sale has already been invoiced. A credit note will be created to reflect the adjustment.',
  paid: 'This sale has been invoiced and paid. A credit note and refund record will be created.'
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)

export default function AdjustmentConfirmationModal({
  open,
  preview,
  loading,
  onConfirm,
  onCancel
}) {
  const [reason, setReason] = useState('')

  if (!open) return null

  // Loading state while calculating preview
  if (loading) {
    return (
      <AlertDialog open onOpenChange={() => {}}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Calculating Adjustment</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              <span>Recalculating prices...</span>
            </div>
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  if (!preview) return null

  const { original, adjusted, delta, invoiceState, strain, trayNumber } = preview
  const hasQuantityChange = original.quantity !== adjusted.quantity
  const hasClassChange = original.productTypeId !== adjusted.productTypeId

  const handleConfirm = () => {
    onConfirm(reason)
    setReason('')
  }

  const handleCancel = () => {
    onCancel()
    setReason('')
  }

  return (
    <AlertDialog open onOpenChange={() => handleCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Adjustment</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          <span>
            Adjusting <strong className="text-neutral-700">{strain}</strong>
            {trayNumber != null && (
              <span className="text-neutral-500"> (tray {trayNumber})</span>
            )}
          </span>
        </AlertDialogDescription>

        {/* Before → After table */}
        <div className="w-full overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-500">
                <th className="pb-2 pr-3 font-medium"></th>
                <th className="pb-2 pr-3 font-medium text-right">Before</th>
                <th className="pb-2 font-medium text-right">After</th>
              </tr>
            </thead>
            <tbody>
              {hasQuantityChange && (
                <tr>
                  <td className="py-1.5 pr-3 text-neutral-500">Quantity</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{original.quantity}kg</td>
                  <td className="py-1.5 text-right tabular-nums font-medium">{adjusted.quantity}kg</td>
                </tr>
              )}
              {hasClassChange && (
                <tr>
                  <td className="py-1.5 pr-3 text-neutral-500">Class</td>
                  <td className="py-1.5 pr-3 text-right">{original.productTypeName}</td>
                  <td className="py-1.5 text-right font-medium">{adjusted.productTypeName}</td>
                </tr>
              )}
              {hasClassChange && original.price !== adjusted.price && (
                <tr>
                  <td className="py-1.5 pr-3 text-neutral-500">Price /kg</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{formatCurrency(original.price)}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium">{formatCurrency(adjusted.price)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-neutral-200 font-medium text-neutral-900">
                <td className="pt-2">Subtotal</td>
                <td className="pt-2 text-right tabular-nums">{formatCurrency(original.subtotal)}</td>
                <td className="pt-2 text-right tabular-nums">{formatCurrency(adjusted.subtotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Financial delta */}
        {delta !== 0 && (
          <div className={`mt-2 px-3 py-2 rounded-md text-sm ${
            delta > 0 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'
          }`}>
            {delta > 0
              ? `Credit of ${formatCurrency(delta)} to customer`
              : `Additional charge of ${formatCurrency(Math.abs(delta))}`
            }
          </div>
        )}

        {/* Invoice state message */}
        <p className="text-xs text-neutral-500 mt-1">
          {INVOICE_STATE_MESSAGES[invoiceState] || INVOICE_STATE_MESSAGES.none}
        </p>

        {/* Reason textarea */}
        <div className="mt-3">
          <label htmlFor="adjustment-reason" className="text-sm font-medium text-neutral-700">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            id="adjustment-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer returned 8 of 10 punnets unsold"
            rows={2}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-neutral-200 focus:outline-2 focus:outline-primary resize-none"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!reason.trim()}
          >
            Apply Adjustment
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
